import Validator from './validator';
import UiTracking from '../tracking/ui-tracking';
import { GenderSelection } from '@parship/psg-interfaces';

export default class GenderSelect {
	private readonly form: HTMLFormElement;
	private readonly usePreselect: boolean;
	private readonly isHetero: boolean;
	private readonly inputCallback: () => void;
	private inputContainer: HTMLElement;
	private genderInputs: NodeListOf<HTMLInputElement>;
	private esSexInputs: NodeListOf<HTMLInputElement>;
	private essex: HTMLInputElement;
	private searchGenders: HTMLInputElement;
	private errorMessage: HTMLElement;
	private onlySearchGenderSelect: boolean;
	private uiTrackingEnabled: boolean;
	private errorCount: number;

	public constructor(
		form: HTMLFormElement,
		usePreselect: boolean,
		isHetero: boolean,
		inputCallback: () => void
	) {
		this.form = form;
		this.usePreselect = usePreselect;
		this.isHetero = isHetero;
		this.inputCallback = inputCallback;
		this.inputContainer = this.form.querySelector('.gender-select');
		this.onlySearchGenderSelect = this.form.classList.contains('search-gender-select');
		this.uiTrackingEnabled = this.form.dataset.biEnabled === 'true';
		this.errorCount = 0;

		this.getDomElements();
		this.setEventListener();
		this.initUiTracking();
	}

	private getMysexValue(): string {
		if (this.onlySearchGenderSelect) {
			return '';
		}
		const mySexInput: HTMLInputElement = this.form.querySelector('input[name="mysex"]:checked');
		return mySexInput.value;
	}

	private getEssexValue(): string {
		const essexInputs: NodeListOf<HTMLInputElement> =
			this.form.querySelectorAll('.essex-input:checked');

		if (essexInputs.length === 0) {
			return '';
		}

		const essexW: HTMLInputElement = this.form.querySelector('input[name="essexW"]');
		const essexM: HTMLInputElement = this.form.querySelector('input[name="essexM"]');

		if (essexW.checked && essexM.checked) {
			return '3';
		}

		return essexW.checked ? essexW.value : essexM.value;
	}

	private getGender(): string {
		if (this.onlySearchGenderSelect) {
			return '';
		}

		const mySexInput: HTMLInputElement = this.form.querySelector('input[name="mysex"]:checked');
		return mySexInput.dataset.gender;
	}

	private getSearchGenders(): string {
		const essexInputs: NodeListOf<HTMLInputElement> =
			this.form.querySelectorAll('.essex-input:checked');

		if (essexInputs.length === 0) {
			return '';
		}

		const searchGenders: string[] = Array.from(essexInputs).map(
			(input) => input.dataset.gender
		);

		return searchGenders.join(',');
	}

	public enableUi(): void {
		this.inputContainer.inert = false;
	}

	public isValidSex(): boolean {
		const mySexValid: boolean = this.onlySearchGenderSelect
			? true
			: Validator.isInputChecked('mysex', this.form);
		const esSexValid: boolean = this.getEssexValue() !== '';

		return mySexValid && esSexValid;
	}

	public validate(): boolean {
		const isValid: boolean = this.isValidSex();

		if (!isValid) {
			this.showError();

			if (this.uiTrackingEnabled) {
				UiTracking.trackUIEvent(
					'registration-form',
					'form',
					'validation-error',
					'gender-select',
					this.errorCount.toString()
				);

				this.errorCount++;
			}
		}

		return isValid;
	}

	public getSelectedSex(): GenderSelection {
		return {
			mySex: this.getMysexValue(),
			esSex: this.getEssexValue(),
			gender: this.getGender(),
			searchGenders: this.getSearchGenders(),
		};
	}

	private getDomElements(): void {
		this.genderInputs = this.form.querySelectorAll(
			'input[type=radio][name=mysex],input[type=checkbox]'
		);
		this.esSexInputs = this.form.querySelectorAll('input[type=checkbox]');
		this.essex = this.form.querySelector('[name=essex]');
		this.searchGenders = this.form.querySelector('[name=searchGenders]');
		this.errorMessage = this.form.querySelector('.gender-error');
	}

	private setEventListener(): void {
		this.esSexInputs.forEach((input: HTMLInputElement) => {
			input.addEventListener('click', (event: MouseEvent) => this.onEssexInput(event));
		});

		this.genderInputs.forEach((input: HTMLInputElement) => {
			input.addEventListener('change', (event: Event) => this.onSexInput(event));
		});
	}

	private onEssexInput(event: Event): void {
		if (this.getEssexValue() === '') {
			event.preventDefault();
			event.stopPropagation();
			return;
		}

		this.esSexInputs.forEach((input: HTMLInputElement) => {
			input.removeAttribute('aria-required');
		});

		this.essex.value = this.getEssexValue();
		this.searchGenders.value = this.getSearchGenders();
	}

	private onSexInput(event: Event): void {
		if (this.usePreselect && !this.isValidSex() && !this.onlySearchGenderSelect) {
			this.preSelectSex(event);
		}

		this.essex.value = this.getEssexValue();
		this.searchGenders.value = this.getSearchGenders();

		if (this.isValidSex()) {
			this.hideError();
		}

		this.inputCallback();
	}

	private preSelectSex(event: Event): void {
		const target: HTMLInputElement = event.target as HTMLInputElement;
		let selector: string;

		if (target.name === 'mysex') {
			if (this.isHetero) {
				selector = target.value === '1' ? 'input[name="essexM"]' : 'input[name="essexW"]';
			} else {
				selector = target.value === '1' ? 'input[name="essexW"]' : 'input[name="essexM"]';
			}
		} else if (this.isHetero) {
			selector =
				target.value === '1'
					? 'input[name="mysex"][value="0"]'
					: 'input[name="mysex"][value="1"]';
		} else {
			selector =
				target.value === '0'
					? 'input[name="mysex"][value="0"]'
					: 'input[name="mysex"][value="1"]';
		}

		const radio: HTMLInputElement = this.form.querySelector(selector);
		radio.checked = true;
	}

	private showError(): void {
		this.errorMessage.classList.add('is-visible');
		this.errorMessage.textContent = this.errorMessage.dataset.errorText;
	}

	public hideError(): void {
		this.errorMessage.textContent = '';
		this.errorMessage.classList.remove('is-visible');
	}

	private initUiTracking(): void {
		if (!this.uiTrackingEnabled) {
			return;
		}

		this.form.querySelectorAll('input[name=mysex]').forEach((input: HTMLInputElement) => {
			input.addEventListener(
				'change',
				(event: Event) => {
					const target: HTMLInputElement = event.target as HTMLInputElement;
					UiTracking.trackUIEvent(
						'registration-form',
						'gender-select',
						event.type,
						'mysex',
						target.dataset.gender || target.value
					);
				},
				{
					once: true,
				}
			);
		});

		this.esSexInputs.forEach((input: HTMLInputElement) => {
			input.addEventListener(
				'change',
				(event: Event) => {
					UiTracking.trackUIEvent(
						'registration-form',
						'gender-select',
						event.type,
						'essex',
						this.getSearchGenders()
					);
				},
				{
					once: true,
				}
			);
		});
	}
}
